import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { NotificationBanner, WeeklyOrders, WeekPicker } from "components";
import { refreshIcon } from "assets/img";
import { ActiveMeals } from "./index";
import { MobXProviderContext, observer } from "mobx-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { PageGuide } from "components";
import classNames from "classnames";

function Home() {
  const { homeStore, userStore, paymentInfoStore } =
    useContext(MobXProviderContext);
  const defaultLocation = userStore.defaultLocation;
  const activeMeals = homeStore.activeMeals;
  const mealsByWeek = homeStore.mealsByWeek;
  const availableMealDates = homeStore.availableMealDates;
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const defaultPaymentMethodId = paymentInfoStore.defaultPaymentMethodId;

  const navigate = useNavigate();

  const fetchAllData = useCallback(() => {
    const startDate = dayjs().format("YYYY-MM-DD");
    const endDate = dayjs().format("YYYY-MM-DD");
    const organizationId = defaultLocation?.organizationId;

    homeStore
      .fetchActiveMeals(
        { start_date: startDate, end_date: endDate },
        organizationId
      )
      .then((response) => {
        if (response?.status === 200) {
          homeStore.fetchNextMealDates({ layout: "all_dates" }, organizationId);

          const startOfWeek = dayjs()
            .tz(defaultLocation?.tzinfo)
            .startOf("week");
          const endOfWeek = dayjs().tz(defaultLocation?.tzinfo).endOf("week");

          homeStore.fetchActiveMealsForWeek(
            {
              start_date: startOfWeek.format("YYYY-MM-DD"),
              end_date: endOfWeek.format("YYYY-MM-DD"),
              layout: "calendar",
            },
            organizationId
          );
        }
      });
  }, [defaultLocation, homeStore]);

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });

    if (defaultLocation?.role !== "team_admin")
      paymentInfoStore.getLocationCreditCards({
        location_id: defaultLocation?.organizationId,
      });
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchDailyMeals = useCallback(
    (date = selectedDate) => {
      const startDate = date.format("YYYY-MM-DD");
      const endDate = date.format("YYYY-MM-DD");
      homeStore.fetchActiveMeals(
        { start_date: startDate, end_date: endDate },
        defaultLocation?.organizationId
      );
    },
    [homeStore, defaultLocation, selectedDate]
  );

  const onDateChange = useCallback(
    (selected) => {
      setSelectedDate(selected);
      fetchDailyMeals(selected);
    },
    [fetchDailyMeals]
  );

  const isUnderReview = useMemo(
    () =>
      activeMeals?.uncatering?.some(
        (meal) => meal?.attributes?.under_review === true
      ),
    [activeMeals.uncatering]
  );

  const anyUnderReviewMeal = activeMeals?.under_review;

  // Reusable Refresh Button Component
  const RefreshButton = () => (
    <button
      onClick={fetchAllData}
      className="flex flex-row bg-light-gray items-center px-4 py-[4px] rounded-lg ml-6"
    >
      <img className="h-4 w-4 mr-[8px]" src={refreshIcon} />
      <small className="text-dark-gray font-inter-regular text-sm">
        Refresh
      </small>
    </button>
  );

  return (
    <div className="rounded-lg p-5 flex flex-col min-w-full">
      {(isUnderReview || anyUnderReviewMeal) && (
        <NotificationBanner
          message="Your uncatering request is under review by hungerhub, this message will disappear once changes have been updated."
          messageType="warning"
          visible={isUnderReview || anyUnderReviewMeal}
          hideClose={true}
          messageLink={isUnderReview && "Contact us"}
          abilityToDismiss={false}
          handleLinkClick={() => userStore.setFreshChatOpen(true)}
        />
      )}

      {!defaultPaymentMethodId &&
        defaultLocation?.invoicePayments == "disabled" && (
          <div className="flex bg-background px-[10px] py-[14px] items-center rounded-xl">
            <div className="mr-2">
              <span className="relative flex h-3 w-3">
                <span
                  className={classNames({
                    "animate-ping absolute inline-flex h-full w-full rounded-full opacity-100": true,
                    "bg-red-700": !defaultPaymentMethodId,
                  })}
                />
                <span
                  className={classNames({
                    "relative inline-flex rounded-full h-3 w-3": true,
                    "bg-red-300": !defaultPaymentMethodId,
                  })}
                />
              </span>
            </div>

            <span className="text-[16px] text-[#2D3958] font-inter-semibold">
              {!defaultPaymentMethodId &&
                "Please update your payment details to be able to finalize the order"}
              {!defaultPaymentMethodId && (
                <button
                  className="text-catering ml-1 font-inter-medium text-[16px]"
                  onClick={() => {
                    defaultLocation?.uncatering
                      ? navigate("/billing/payment-info")
                      : navigate("/billing/marketplace/payment-info");
                  }}
                >
                  Update payment details
                </button>
              )}
            </span>
          </div>
        )}

      <div className="my-[24px] d-row justify-between">
        <h1 className="text-dark-gray font-inter-semibold text-[32px]">
          Today
        </h1>

        <PageGuide />
      </div>

      <div className="hidden lg:flex flex-row justify-between">
        <WeekPicker
          dayPicker={true}
          onDateChange={onDateChange}
          currentDate={selectedDate}
          setCurrentDate={setSelectedDate}
        />
        <RefreshButton />
      </div>

      <div className="hidden lg:hidden md:block mt-[32px]">
        <div className="flex flex-row w-full">
          <RefreshButton />
        </div>

        <div className="mt-[32px]">
          <WeekPicker
            dayPicker={true}
            width="w-full"
            onDateChange={onDateChange}
            currentDate={selectedDate}
            setCurrentDate={setSelectedDate}
          />
        </div>
      </div>

      <ActiveMeals
        data={activeMeals}
        locationId={defaultLocation?.organizationId}
        availableDates={availableMealDates}
        currentDate={selectedDate}
        onDateChange={onDateChange}
      />

      <div className="mt-[30px] mb-[24px] d-row justify-between">
        <h2 className="font-inter-bold text-dark-gray text-[20px]">
          This week orders
        </h2>

        <button
          onClick={() => navigate("/meal-management")}
          className="btn-blue-accent rounded-lg"
        >
          Manage Meal Plans
        </button>
      </div>

      <WeeklyOrders
        data={mealsByWeek}
        locationId={defaultLocation?.organizationId}
        navigate={navigate}
        defaultLocation={defaultLocation}
      />
    </div>
  );
}

export default observer(Home);
